import { MutationConfig, MutationFunction, MutationResult, MutationResultPair } from './types/dataMutation';
import { useEffect } from 'react';
import { useDataMutationProvider } from './useDataMutationProvider';
import { useMutationV5 } from './adapters/useMutationV5';
import { QueryKey } from './types';

function useMemoMutation({
	queryKey,
	mutationData,
}: {
	queryKey?: QueryKey;
	mutationData: MutationResult<unknown, unknown>;
}) {
	const hasQueryKey = typeof queryKey !== 'undefined';
	const queryHash = JSON.stringify(queryKey);
	const { addMutation, removeMutation } = useDataMutationProvider();

	useEffect(() => {
		if (!hasQueryKey) {
			return;
		}

		addMutation({
			key: queryHash,
			result: {
				isError: mutationData.isError,
				isLoading: mutationData.isLoading,
				isSuccess: mutationData.isSuccess,
				status: mutationData.status,
			},
		});

		return () => {
			removeMutation(queryHash);
		};
	}, [
		addMutation,
		queryHash,
		hasQueryKey,
		mutationData.isError,
		mutationData.isLoading,
		mutationData.isSuccess,
		mutationData.status,
		removeMutation,
	]);
}

export function useDataMutation<TResult, TError = unknown, TVariables = undefined, TSnapshot = unknown>(
	mutationFn: MutationFunction<TResult, TVariables>,
	config?: MutationConfig<TResult, TError, TVariables, TSnapshot>,
): MutationResultPair<TResult, TError, TVariables, TSnapshot>;

export function useDataMutation(mutationFn, config) {
	const [mutation, mutationData] = useMutationV5(mutationFn, config);

	useMemoMutation({ queryKey: config?.queryKey, mutationData });

	return [mutation, mutationData];
}
